// The typed payloads of the seven REVIEW OUTBOX kinds (plan 102, review outbox slice 03,
// incident developerz-ai/ultimate#969): the box freezes a finished review into
// `review_outbox`, and the worker's drain posts it to the PR one paced part at a time. Each
// write the drain makes on the PR, and each decision about what it will not post, is one
// row. The writers are the worker's (`fleet/review-outbox.ts`, `review/outbox-drain.ts`); the
// kinds and their shapes are here so a writer and the Terminal renderer read ONE contract.
// Stored vocabulary: a field is never renamed once a row carries it.
//
// Every row is `visibleToMaintainer: true`: each one is a write on, or a decision about, a
// maintainer's pull request, and audit is trust. Every row's `input` is the same
// `ReviewOutboxAuditInputSchema` (the account, the PR, the run and the outbox); its `output`
// names what was posted, retired or decided. The closed enums come from
// `../../value-objects/review-outbox`, the one source the `review_outbox` CHECKs read too.

import { z } from 'zod';
import {
  REVIEW_OUTBOX_COMMENT_RETIRED_REASONS,
  REVIEW_OUTBOX_EVENTS,
  REVIEW_OUTBOX_RETIRED_REASONS,
  REVIEW_OUTBOX_WIRE_DOWNGRADES,
} from '../../value-objects/review-outbox';

const RepoFullName = z.string().regex(/^[^/\s]+\/[^/\s]+$/);
const GithubNumber = z.number().int().positive();
const Count = z.number().int().nonnegative();
/** A GitHub review id; null when the write did not return one. */
const ReviewId = z.number().int().nullable();

/** The `input` every `review.outbox_*` row carries. `outboxId` is `review_outbox.id`. */
export const ReviewOutboxAuditInputSchema = z.object({
  accountId: z.string().min(1),
  repo: RepoFullName,
  prNumber: GithubNumber,
  taskId: z.string().min(1),
  runId: z.string().min(1),
  outboxId: z.string().regex(/^rvo_[0-9a-f]{32}$/),
});
export type ReviewOutboxAuditInput = z.infer<typeof ReviewOutboxAuditInputSchema>;

/** `review.outbox_enqueued` — the box sealed its review: `total` comments, the frozen `event`. */
export const ReviewOutboxEnqueuedOutputSchema = z.object({
  total: Count,
  event: z.enum(REVIEW_OUTBOX_EVENTS),
});
export type ReviewOutboxEnqueuedOutput = z.infer<typeof ReviewOutboxEnqueuedOutputSchema>;

/**
 * `review.outbox_part_posted` — one part landed on the PR. `part` is its k (from GitHub's own
 * markers, never a held counter), `comments` the inline comments it carried, `final` whether
 * it was the marked review. `posted`/`total` are the outbox's running tally after it;
 * `commitId` the commit it was pinned to, and `reanchored` whether that was the live head
 * because the head moved under a byte-identical file diff.
 */
export const ReviewOutboxPartPostedOutputSchema = z.object({
  part: z.number().int().min(1),
  comments: Count,
  reviewId: ReviewId,
  final: z.boolean(),
  posted: Count,
  total: Count,
  commitId: z.string().regex(/^[0-9a-f]{40}$/),
  reanchored: z.boolean(),
});
export type ReviewOutboxPartPostedOutput = z.infer<typeof ReviewOutboxPartPostedOutputSchema>;

/** `review.outbox_reshaped` — a secondary rate limit shrank comments per post `from` -> `to`. */
export const ReviewOutboxReshapedOutputSchema = z.object({
  from: z.number().int().min(1),
  to: z.number().int().min(1),
});
export type ReviewOutboxReshapedOutput = z.infer<typeof ReviewOutboxReshapedOutputSchema>;

/**
 * `review.outbox_comment_retired` — these comments (by `ordinal`) will not be posted, and
 * why: a retirement reason, or `deduped` (the same finding is already on the PR at the same
 * line from ANOTHER review of ours, plan 102 03 step 4). Every non-inline exit is a named,
 * audited one.
 */
export const ReviewOutboxCommentRetiredOutputSchema = z.object({
  reason: z.enum([...REVIEW_OUTBOX_COMMENT_RETIRED_REASONS, 'deduped']),
  ordinals: z.array(Count).min(1),
});
export type ReviewOutboxCommentRetiredOutput = z.infer<
  typeof ReviewOutboxCommentRetiredOutputSchema
>;

/**
 * `review.outbox_delivered` — the final review is on the PR. `reviewEvent` is what GitHub
 * received; `wireDowngrade` names why it differs from the frozen event (null when it does
 * not). `reconciled` = the final review was found already on the PR by its marker, and
 * nothing was re-posted.
 */
export const ReviewOutboxDeliveredOutputSchema = z.object({
  reviewId: ReviewId,
  posted: Count,
  total: Count,
  reviewEvent: z.enum(REVIEW_OUTBOX_EVENTS),
  wireDowngrade: z.enum(REVIEW_OUTBOX_WIRE_DOWNGRADES).nullable(),
  reconciled: z.boolean(),
});
export type ReviewOutboxDeliveredOutput = z.infer<typeof ReviewOutboxDeliveredOutputSchema>;

/**
 * `review.outbox_retired` — the outbox ended unposted. `posted` says how much landed first.
 * `reconciled: false` = reading back what landed was impossible (`installation_gone`).
 */
export const ReviewOutboxRetiredOutputSchema = z.object({
  reason: z.enum(REVIEW_OUTBOX_RETIRED_REASONS),
  posted: Count,
  total: Count,
  reconciled: z.boolean(),
});
export type ReviewOutboxRetiredOutput = z.infer<typeof ReviewOutboxRetiredOutputSchema>;

/**
 * `review.outbox_dead` — the drain gave up after `attempts`; the PR stays held and the row is
 * never silently delivered or dropped. `lastError` is bounded at the writer (500 chars).
 */
export const ReviewOutboxDeadOutputSchema = z.object({
  attempts: z.number().int().min(1),
  lastError: z.string().max(500),
  posted: Count,
  total: Count,
});
export type ReviewOutboxDeadOutput = z.infer<typeof ReviewOutboxDeadOutputSchema>;
